/**
 * Everything the stats surfaces show but nobody stores: rates and averages
 * folded out of the counters `memberStats` keeps. Pure, so the server, the
 * owner console and the TV all derive the same numbers from the same row
 * rather than each rolling their own arithmetic.
 */
import type { MemberStatsView } from './types.js';

/** A stats row with every counter zeroed — a game the member never played. */
export const EMPTY_STATS: MemberStatsView = {
  hands: 0,
  wins: 0,
  losses: 0,
  pushes: 0,
  blackjacks: 0,
  surrenders: 0,
  doubles: 0,
  splits: 0,
  sideBetsWon: 0,
  potsWon: 0,
  showdowns: 0,
  biggestWin: 0,
  biggestPot: 0,
  net: 0,
  rounds: 0,
  wagered: 0,
  returned: 0,
  biggestLoss: 0,
  bestRound: 0,
  worstRound: 0,
  winStreak: 0,
  lossStreak: 0,
  bestWinStreak: 0,
  worstLossStreak: 0,
  sideBetsPlaced: 0,
  busts: 0,
  folds: 0,
  allIns: 0,
  vpip: 0,
  naturals: 0,
  straightUpHits: 0,
  ticketsPlaced: 0,
  bestCatch: 0,
  pointsMade: 0,
  sevenOuts: 0,
  rollsShot: 0,
  firstPlayedAt: null,
  lastPlayedAt: null,
};

/**
 * The rates a stats row implies. Every field is null rather than 0 when its
 * denominator is empty — "no hands yet" and "lost every hand" are different
 * facts, and a 0% shown for the first is a lie the surfaces would repeat.
 */
export interface DerivedStats {
  /** wins ÷ decided hands. Pushes are not decisions, so they are excluded. */
  winRate: number | null;
  /** net ÷ wagered: chips kept per chip pushed out. */
  roi: number | null;
  /** wagered ÷ rounds. */
  avgBet: number | null;
  /** net ÷ rounds: the expected swing of sitting down for one more. */
  avgResult: number | null;
  /** sideBetsWon ÷ sideBetsPlaced. */
  sideBetHitRate: number | null;
  /** blackjacks ÷ hands — the natural rate, ~4.8% under normal rules. */
  blackjackRate: number | null;
  /** busts ÷ hands. */
  bustRate: number | null;
  /** potsWon ÷ hands (Hold'em). */
  potWinRate: number | null;
  /** Minutes between the first and last settled round. Null before two. */
  minutesPlayed: number | null;
  /** rounds ÷ hour, over `minutesPlayed`. */
  roundsPerHour: number | null;
}

const rate = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? numerator / denominator : null;

/** Fold a stats row out into its rates. Never throws, never divides by zero. */
export function deriveStats(stats: MemberStatsView): DerivedStats {
  const decided = stats.wins + stats.losses;
  const minutesPlayed =
    stats.firstPlayedAt && stats.lastPlayedAt
      ? Math.max(
          0,
          (Date.parse(stats.lastPlayedAt) - Date.parse(stats.firstPlayedAt)) / 60_000,
        )
      : null;
  return {
    winRate: rate(stats.wins, decided),
    roi: rate(stats.net, stats.wagered),
    avgBet: rate(stats.wagered, stats.rounds),
    avgResult: rate(stats.net, stats.rounds),
    sideBetHitRate: rate(stats.sideBetsWon, stats.sideBetsPlaced),
    blackjackRate: rate(stats.blackjacks, stats.hands),
    bustRate: rate(stats.busts, stats.hands),
    potWinRate: rate(stats.potsWon, stats.hands),
    minutesPlayed,
    // A single round spans no time, so a rate off it would be nonsense.
    roundsPerHour:
      minutesPlayed && minutesPlayed > 0 ? (stats.rounds / minutesPlayed) * 60 : null,
  };
}

/**
 * Fold several games' rows into one. Counters add, `biggest*` / `best*` keep
 * their high-water marks, `worstRound` keeps its low-water mark, and the
 * timestamps take the outer bounds.
 *
 * Streaks are the exception: a cross-game streak would have to interleave
 * rounds from tables that ran at once, which the per-game rows cannot
 * reconstruct. The totals take the longest streak any one game held, which
 * is the honest reading of "their best run tonight".
 */
export function sumStats(rows: readonly MemberStatsView[]): MemberStatsView {
  return rows.reduce<MemberStatsView>(
    (a, b) => ({
      hands: a.hands + b.hands,
      wins: a.wins + b.wins,
      losses: a.losses + b.losses,
      pushes: a.pushes + b.pushes,
      blackjacks: a.blackjacks + b.blackjacks,
      surrenders: a.surrenders + b.surrenders,
      doubles: a.doubles + b.doubles,
      splits: a.splits + b.splits,
      sideBetsWon: a.sideBetsWon + b.sideBetsWon,
      potsWon: a.potsWon + b.potsWon,
      showdowns: a.showdowns + b.showdowns,
      biggestWin: Math.max(a.biggestWin, b.biggestWin),
      biggestPot: Math.max(a.biggestPot, b.biggestPot),
      net: a.net + b.net,
      rounds: a.rounds + b.rounds,
      wagered: a.wagered + b.wagered,
      returned: a.returned + b.returned,
      biggestLoss: Math.max(a.biggestLoss, b.biggestLoss),
      bestRound: Math.max(a.bestRound, b.bestRound),
      worstRound: Math.min(a.worstRound, b.worstRound),
      winStreak: Math.max(a.winStreak, b.winStreak),
      lossStreak: Math.max(a.lossStreak, b.lossStreak),
      bestWinStreak: Math.max(a.bestWinStreak, b.bestWinStreak),
      worstLossStreak: Math.max(a.worstLossStreak, b.worstLossStreak),
      sideBetsPlaced: a.sideBetsPlaced + b.sideBetsPlaced,
      busts: a.busts + b.busts,
      folds: a.folds + b.folds,
      allIns: a.allIns + b.allIns,
      vpip: a.vpip + b.vpip,
      naturals: a.naturals + b.naturals,
      straightUpHits: a.straightUpHits + b.straightUpHits,
      ticketsPlaced: a.ticketsPlaced + b.ticketsPlaced,
      bestCatch: Math.max(a.bestCatch, b.bestCatch),
      pointsMade: a.pointsMade + b.pointsMade,
      sevenOuts: a.sevenOuts + b.sevenOuts,
      rollsShot: a.rollsShot + b.rollsShot,
      firstPlayedAt: earlier(a.firstPlayedAt, b.firstPlayedAt),
      lastPlayedAt: later(a.lastPlayedAt, b.lastPlayedAt),
    }),
    EMPTY_STATS,
  );
}

const earlier = (a: string | null, b: string | null): string | null =>
  a === null ? b : b === null ? a : a < b ? a : b;

const later = (a: string | null, b: string | null): string | null =>
  a === null ? b : b === null ? a : a > b ? a : b;
